"use client";

import {
  SankeyChart,
  SankeyNode,
  SankeyLink,
  SankeyTooltip,
  type SankeyNodeDatum,
} from "@/components/charts/sankey";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { useLegendClickSync } from "@/components/charts/use-legend-click-sync";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
} from "@/components/charts/legend";
import { cn } from "@/lib/utils";
import { ModuleChartSankeyData } from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { chartPaletteStyle } from "../imports-token-scope";
import { JSX, useCallback, useMemo, useState } from "react";
import { CHART_PLOT_HEIGHT, CHART_PLOT_WIDTH } from "@/config/layout";

const DEFAULT_DATA: ModuleChartSankeyData = {
  nodes: [
    { name: "Organic Search", category: "source" },
    { name: "Paid Ads", category: "source" },
    { name: "Direct", category: "source" },
    { name: "Homepage", category: "landing" },
    { name: "Product Page", category: "landing" },
    { name: "Signed Up", category: "outcome" },
    { name: "Bounced", category: "outcome" },
  ],
  links: [
    { source: 0, target: 3, value: 120 },
    { source: 0, target: 4, value: 60 },
    { source: 1, target: 4, value: 80 },
    { source: 2, target: 3, value: 40 },
    { source: 3, target: 5, value: 90 },
    { source: 3, target: 6, value: 70 },
    { source: 4, target: 5, value: 100 },
    { source: 4, target: 6, value: 40 },
  ],
};

const CHART_HEIGHT = CHART_PLOT_HEIGHT.sankey;
const CHART_WIDTH = CHART_PLOT_WIDTH.sankey;

type SankeyCategory = NonNullable<SankeyNodeDatum["category"]>;

const CATEGORY_ORDER = ["source", "landing", "outcome"] as const;
const CATEGORY_LABEL: Record<SankeyCategory, string> = {
  source: "Source",
  landing: "Landing",
  outcome: "Outcome",
};
const CATEGORY_COLOR: Record<SankeyCategory, string> = {
  source: "var(--chart-1)",
  landing: "var(--chart-2)",
  outcome: "var(--chart-3)",
};
const FALLBACK_NODE_COLOR = "var(--chart-4)";

export default function ModuleSankeyChart({
  data = DEFAULT_DATA,
  grayscale,
  accent,
  className,
}: {
  data?: ModuleChartSankeyData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  // One list drives both the legend items and the index lookup, so the legend's
  // activeIndex maps back onto a category structurally, not by coincidence.
  const presentCategories = useMemo(() => {
    const present = new Set(
      data.nodes
        .map((n) => n.category)
        .filter((c): c is SankeyCategory => !!c),
    );
    return CATEGORY_ORDER.filter((c) => present.has(c));
  }, [data.nodes]);

  const legendItems = useMemo(
    () =>
      presentCategories.map((c) => ({
        label: CATEGORY_LABEL[c],
        value: 0,
        color: CATEGORY_COLOR[c],
      })),
    [presentCategories],
  );

  const [legendHovered, setLegendHovered] = useState<number | null>(null);
  const { activeIndex, handleSelect, setLabelRef } = useLegendClickSync();

  const selectedCategory =
    activeIndex == null ? null : (presentCategories[activeIndex] ?? null);

  const getNodeColor = useCallback(
    (node: { category?: SankeyCategory }) =>
      node.category ? CATEGORY_COLOR[node.category] : FALLBACK_NODE_COLOR,
    [],
  );

  return (
    <div className={cn("flex w-full flex-col gap-3", className)} style={chartPaletteStyle(grayscale, accent)}>
      <SwipeScroll className="w-full" fade="auto">
        <div
          className="shrink-0"
          style={{ width: CHART_WIDTH, height: CHART_HEIGHT }}
        >
          <SankeyChart
            aspectRatio="auto"
            data={{
              ...data,
              nodes: data.nodes.map((n) => ({ ...n, name: stripInlineMarkdown(n.name) })),
            }}
            margin={{ top: 28, right: 150, bottom: 28, left: 150 }}
            nodeWidth={16}
            nodePadding={24}
          >
            <SankeyLink
              useGradient
              strokeOpacity={0.7}
              getNodeColor={getNodeColor}
              selectedCategory={selectedCategory}
            />
            <SankeyNode
              lineCap={4}
              showLabels
              getNodeColor={getNodeColor}
              selectedCategory={selectedCategory}
            />
            <SankeyTooltip />
          </SankeyChart>
        </div>
      </SwipeScroll>

      {legendItems.length > 0 && (
        <SwipeScroll className="w-full pb-1" fade="auto">
          <Legend
            items={legendItems}
            hoveredIndex={legendHovered}
            onHoverChange={setLegendHovered}
            selectedIndex={activeIndex}
            onSelect={handleSelect}
            onLabelRef={setLabelRef}
            className="flex flex-row gap-6"
          >
            <LegendItem className="flex shrink-0 items-center gap-2">
              <LegendMarker className="h-2.5 w-2.5" />
              <LegendLabel />
            </LegendItem>
          </Legend>
        </SwipeScroll>
      )}
    </div>
  );
}
